import { useCallback, useEffect, useRef, useState } from "react";
import CommonService from "@/services/common.service.ts";
import commonService from "@/services/common.service.ts";
import { FileContent } from "../utils/file-converters";

/**
 * Custom hook for managing streaming file content
 */
export const useStreamingManager = (projectId: string | undefined) => {
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [htmlInProgress, setHtmlInProgress] = useState<boolean>(false);
    const fileContents = useRef<Map<string, FileContent>>(new Map());
    const currentFilePath = useRef<string | null>(null);

    const processChunk = useCallback((chunk: string) => {
        if (!isStreaming) {
            setIsStreaming(true);
        }

        try {
            const message = commonService.processStream(chunk);
            const eventType = message?.type;
            const eventData = message?.data;
            const path = eventData?.path;

            // Process HTML, CSS, and JS files only
            if (!path || !(/\.(html|css|js)$/.test(path))) return;

            switch (eventType) {
                case "file_start":
                    currentFilePath.current = path;
                    fileContents.current.set(path, {
                        path: path,
                        content: "",
                        completed: false
                    });

                    if (path.endsWith('index.html')) {
                        setHtmlInProgress(true);
                    }
                    break;

                case "file_delta":
                    if (currentFilePath.current && typeof eventData?.delta === "string") {
                        const currentFile = fileContents.current.get(currentFilePath.current);
                        if (currentFile) {
                            currentFile.content += eventData.delta;
                            fileContents.current.set(currentFilePath.current, currentFile);
                        }
                    }
                    break;

                case "file_end":
                    if (currentFilePath.current) {
                        const currentFile = fileContents.current.get(currentFilePath.current);
                        if (currentFile) {
                            currentFile.completed = true;
                            fileContents.current.set(currentFilePath.current, currentFile);

                            if (currentFilePath.current.endsWith('index.html')) {
                                setHtmlInProgress(false);
                            }
                        }
                        currentFilePath.current = null;
                    }
                    break;

                default:
                    // Ignore unknown events
            }
        } catch (err) {
            console.error("Failed to process chunk:", err);
        }
    }, [isStreaming]);

    const resetStreaming = useCallback(() => {
        setIsStreaming(false);
        setHtmlInProgress(false);
        fileContents.current.clear();
        currentFilePath.current = null;
    }, []);

    // Initialize streaming event listeners
    useEffect(() => {
        const subscriptions = [
            CommonService._communications.handleCodeStreamerSubject.subscribe(processChunk),
            CommonService._communications.startCodeStreamerSubject.subscribe((value: boolean) => {
                if (value) {
                    setIsStreaming(true);
                    setHtmlInProgress(false);
                    fileContents.current.clear();
                }
            }),
            CommonService._communications.endCodeStreamerSubject.subscribe((value: boolean) => {
                if (value) {
                    setTimeout(() => {
                        setIsStreaming(false);
                        setHtmlInProgress(false);
                        currentFilePath.current = null;
                    }, 500);
                }
            })
        ];

        return () => {
            subscriptions.forEach(sub => sub.unsubscribe());
        };
    }, [projectId, processChunk]);

    return {
        isStreaming,
        htmlInProgress,
        fileContents: fileContents.current,
        resetStreaming
    };
}; 